// src/pages/merchant/dashboard/Overview.js
import React, { useEffect, useState, useRef } from "react";
import {
  Store,
  User,
  Briefcase,
  Star,
  Calendar,
  Clock,
  MapPin,
  Link2,
  Phone,
  MessageCircle,
  Plus,
  Edit,
  Save,
  X,
  Building2,
  Globe,
  Award,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Mail,
  Tag,
  Search,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboard,
  updateOverview,
  fetchKeywords,
  createKeyword,
  updateMerchantKeywords,
} from "../../../features/merchant/dashboardThunks";
import { toast } from "react-toastify";
import "./dashboard.css";

/* ===================== HELPERS ===================== */

const normalizeUrl = (url) => {
  if (!url) return "";
  const t = url.trim();
  if (!t) return "";
  return t.startsWith("http://") || t.startsWith("https://")
    ? t
    : `https://${t}`;
};

const toTimeInputFormat = (time) => {
  if (!time) return "";
  if (/^\d{2}:\d{2}$/.test(time)) return time;
  const d = new Date(`1970-01-01 ${time}`);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/* ===================== COMPONENT ===================== */

const BusinessOverview = () => {
  const dispatch = useDispatch();
  const { data, loading, updating, allKeywords, keywordsLoading } = useSelector((s) => s.dashboard);

  const [documentId, setDocumentId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    businessName: "",
    ownerName: "",
    gst: "",
    email: "",
    rating: "",
    experience: 0,
    startTime: "",
    endTime: "",
    businessType: "",
    businessCategory: "",
    address: "",
    locationUrl: "",
    contactNumber: "",
    whatsappNumber: "",
    socialLinks: [""],
  });

  // ===== KEYWORDS STATE =====
  const [selectedKeywords, setSelectedKeywords] = useState([]); // [{_id, name}]
  const [keywordSearch, setKeywordSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [keywordSaving, setKeywordSaving] = useState(false);
  const dropdownRef = useRef(null);

  /* ===================== FETCH DASHBOARD ===================== */

  useEffect(() => {
    dispatch(fetchDashboard());
    dispatch(fetchKeywords());
  }, [dispatch]);

  useEffect(() => {
    if (!data) return;

    setDocumentId(data.documentId);

    setFormData({
      businessName: data.businessDetails?.businessName || "",
      ownerName: data.businessDetails?.ownerName || "",
      businessType: data.businessDetails?.businessType || "",
      businessCategory: data.businessDetails?.businessCategory || "",
      gst: data.overview?.gst || data.businessDetails?.gst || "N/A",
      email: data.overview?.email || data.businessDetails?.email || "N/A",
      rating: data.overview?.rating ?? "",
      experience: Number(data.overview?.experienceInYears) || 0,
      startTime: toTimeInputFormat(data.overview?.workingHoursStart),
      endTime: toTimeInputFormat(data.overview?.workingHoursEnd),
      address: data.overview?.address || "",
      locationUrl: data.overview?.locationURL || "",
      contactNumber: data.overview?.contactNumber || "",
      whatsappNumber: data.overview?.whatsappNumber || "",
      socialLinks:
        data.overview?.socialMediaLinks?.length > 0
          ? data.overview.socialMediaLinks
          : [""],
    });

    // Set selected keywords from dashboard data
    if (data.businessKeywords?.length > 0) {
      setSelectedKeywords(data.businessKeywords);
    }
  }, [data]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch keywords when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchKeywords(keywordSearch));
    }, 300);
    return () => clearTimeout(timer);
  }, [keywordSearch, dispatch]);

  /* ===================== HANDLERS ===================== */

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSocialLinkChange = (index, value) => {
    const updated = [...formData.socialLinks];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, socialLinks: updated }));
  };

  const addSocialLink = () =>
    setFormData((prev) => ({
      ...prev,
      socialLinks: [...prev.socialLinks, ""],
    }));

  const removeSocialLink = (index) => {
    if (formData.socialLinks.length > 1) {
      setFormData((prev) => ({
        ...prev,
        socialLinks: prev.socialLinks.filter((_, i) => i !== index),
      }));
    }
  };

  /* ===================== KEYWORD HANDLERS ===================== */

  const isKeywordSelected = (id) => selectedKeywords.some((k) => k._id === id);

  const addKeyword = (kw) => {
    if (!isKeywordSelected(kw._id)) {
      setSelectedKeywords((prev) => [...prev, kw]);
    }
    setKeywordSearch("");
    setShowDropdown(false);
  };

  const removeKeyword = (id) => {
    setSelectedKeywords((prev) => prev.filter((k) => k._id !== id));
  };

  const handleCreateAndAdd = async () => {
    const name = keywordSearch.trim().toLowerCase();
    if (!name) return;
    const res = await dispatch(createKeyword(name));
    if (createKeyword.fulfilled.match(res)) {
      const kw = res.payload?.data;
      if (kw?._id) addKeyword(kw);
    } else {
      toast.error(res.payload || "Failed to create keyword");
    }
  };

  const handleSaveKeywords = async () => {
    setKeywordSaving(true);
    const ids = selectedKeywords.map((k) => k._id);
    const res = await dispatch(updateMerchantKeywords(ids));
    if (updateMerchantKeywords.fulfilled.match(res)) {
      toast.success("Keywords saved!");
    } else {
      toast.error(res.payload || "Failed to save keywords");
    }
    setKeywordSaving(false);
  };

  // Filtered dropdown list (exclude already selected)
  const filteredKeywords = allKeywords.filter(
    (k) => !isKeywordSelected(k._id)
  );

  const exactMatch = allKeywords.find(
    (k) => k.name === keywordSearch.trim().toLowerCase()
  );

  /* ===================== SAVE OVERVIEW ===================== */

  const handleSave = async () => {
    if (!documentId) return;

    const payload = {};
    if (formData.email.trim()) {
      payload.email = formData.email.trim();
    }
    payload.experienceInYears = Number(formData.experience) || 0;
    if (formData.startTime) payload.workingHoursStart = formData.startTime;
    if (formData.endTime) payload.workingHoursEnd = formData.endTime;
    if (formData.address.trim()) payload.address = formData.address.trim();
    const location = normalizeUrl(formData.locationUrl);
    if (location) payload.locationURL = location;
    if (formData.contactNumber.trim()) payload.contactNumber = formData.contactNumber.trim();
    if (formData.whatsappNumber.trim()) payload.whatsappNumber = formData.whatsappNumber.trim();
    const socialLinks = formData.socialLinks.map(normalizeUrl).filter(Boolean);
    if (socialLinks.length > 0) payload.socialMediaLinks = socialLinks;

    try {
      await dispatch(updateOverview({ documentId, payload })).unwrap();
      setIsEditing(false);
    } catch (err) {
      console.error("Update overview failed:", err);
    }
  };

  if (loading) return null;

  /* ===================== RENDER ===================== */

  return (
    <div className="enhanced-dashboard">
      {/* Page Header */}
      <div className="page-header-banner">
        <div className="header-content-wrapper">
          <div className="header-icon-box">
            <Building2 size={28} />
          </div>
          <div className="header-text-content">
            <h1 className="header-main-heading">Business Overview</h1>
            <p className="header-sub-text">
              Manage your business portfolio and contact information
            </p>
          </div>
        </div>
        <div className={`status-indicator ${isEditing ? "editing" : "viewing"}`}>
          {isEditing ? (
            <>
              <AlertCircle size={16} />
              <span>Editing Mode</span>
            </>
          ) : (
            <>
              <CheckCircle size={16} />
              <span>View Mode</span>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-content-area">
        {/* Business Identity Section */}
        <div className="enhanced-section-card business-identity-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-purple">
                <Store size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Business Portfolio</h2>
                <p className="section-subtitle">Your registered business information</p>
              </div>
            </div>
          </div>

          <div className="identity-cards-grid">
            <div className="identity-card">
              <div className="identity-card-icon">
                <Store size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">Business Name</span>
                <span className="identity-value">{formData.businessName}</span>
              </div>
            </div>

            <div className="identity-card">
              <div className="identity-card-icon user-icon">
                <User size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">Owner Name</span>
                <span className="identity-value">{formData.ownerName}</span>
              </div>
            </div>

            <div className="identity-card">
              <div className="identity-card-icon type-icon">
                <Briefcase size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">Business Type</span>
                <span className="identity-value">{formData.businessType}</span>
              </div>
            </div>

            <div className="identity-card">
              <div className="identity-card-icon category-icon">
                <Award size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">Category</span>
                <span className="identity-value">{formData.businessCategory}</span>
              </div>
            </div>

            <div className="identity-card">
              <div className="identity-card-icon gstin-icon">
                <Briefcase size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">GSTIN</span>
                <span className="identity-value mono-text">{formData.gst}</span>
              </div>
            </div>

            <div className="identity-card">
              <div className="identity-card-icon user-icon">
                <Mail size={24} />
              </div>
              <div className="identity-card-content">
                <span className="identity-label">Email</span>
                {isEditing ? (
                  <input
                    type="email"
                    className="identity-input"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="Enter email address"
                  />
                ) : (
                  <span className="identity-value">{formData.email || "—"}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Experience & Availability Section */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-blue">
                <TrendingUp size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Operating Info</h2>
                <p className="section-subtitle">Your expertise and working hours</p>
              </div>
            </div>
          </div>

          <div className="stats-cards-row">
            <div className="stat-card rating-stat">
              <div className="stat-icon-wrapper">
                <Star size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Average Rating</span>
                <div className="stat-input-wrapper">
                  <input
                    type="number"
                    className="stat-input"
                    value={formData.rating}
                    onChange={(e) => handleInputChange("rating", e.target.value)}
                    disabled={true}
                    placeholder="0.0"
                    min="0"
                    max="5"
                    step="0.1"
                  />
                  <span className="stat-suffix">/5</span>
                </div>
              </div>
            </div>

            <div className="stat-card experience-stat">
              <div className="stat-icon-wrapper experience-icon">
                <Calendar size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Years of Experience</span>
                <div className="stat-input-wrapper">
                  <input
                    type="number"
                    className="stat-input"
                    value={formData.experience}
                    onChange={(e) => handleInputChange("experience", e.target.value)}
                    disabled={!isEditing}
                    placeholder="0"
                    min="0"
                  />
                  <span className="stat-suffix">years</span>
                </div>
              </div>
            </div>
          </div>

          <div className="working-hours-section">
            <h3 className="subsection-title">
              <Clock size={18} />
              Working Hours
            </h3>
            <div className="hours-grid">
              <div className="hour-input-group">
                <label className="hour-label">Opens At</label>
                <div className="hour-input-wrapper">
                  <Clock size={18} className="hour-icon" />
                  <input
                    type="time"
                    className="hour-input"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange("startTime", e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="hour-divider">
                <span>to</span>
              </div>
              <div className="hour-input-group">
                <label className="hour-label">Closes At</label>
                <div className="hour-input-wrapper">
                  <Clock size={18} className="hour-icon" />
                  <input
                    type="time"
                    className="hour-input"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange("endTime", e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-green">
                <MapPin size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Location & Contact</h2>
                <p className="section-subtitle">How customers can reach you</p>
              </div>
            </div>
          </div>

          <div className="contact-form-grid">
            <div className="form-group-enhanced full-width">
              <label className="form-label-enhanced">
                <MapPin size={16} /> Full Address
              </label>
              <textarea
                className="form-textarea-enhanced"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                disabled={!isEditing}
                placeholder="Enter your complete business address..."
                rows={3}
              />
            </div>
            <div className="form-group-enhanced full-width">
              <label className="form-label-enhanced">
                <Globe size={16} />
                Google Maps Link
              </label>
              <div className="input-with-icon">
                <Link2 size={18} className="input-icon-left" />
                <input
                  type="url"
                  className="form-input-enhanced with-icon"
                  value={formData.locationUrl}
                  onChange={(e) => handleInputChange("locationUrl", e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>

            <div className="form-group-enhanced">
              <label className="form-label-enhanced">
                <Phone size={16} />
                Phone Number
              </label>
              <div className="input-with-icon">
                <Phone size={18} className="input-icon-left" />
                <input
                  type="tel"
                  className="form-input-enhanced with-icon"
                  value={formData.contactNumber}
                  onChange={(e) => handleInputChange("contactNumber", e.target.value)}
                  disabled={!isEditing}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>

            <div className="form-group-enhanced">
              <label className="form-label-enhanced">
                <MessageCircle size={16} />
                WhatsApp Number
              </label>
              <div className="input-with-icon">
                <MessageCircle size={18} className="input-icon-left" />
                <input
                  type="tel"
                  className="form-input-enhanced with-icon"
                  value={formData.whatsappNumber}
                  onChange={(e) => handleInputChange("whatsappNumber", e.target.value)}
                  disabled={!isEditing}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
            </div>
          </div>

          {/* Social Links Section */}
          <div className="social-links-section">
            <h3 className="subsection-title">
              <Globe size={18} />
              Social Media Links
            </h3>
            <div className="social-links-list">
              {formData.socialLinks.map((link, index) => (
                <div key={index} className="social-link-row">
                  <div className="social-link-number">{index + 1}</div>
                  <div className="input-with-icon flex-grow">
                    <Link2 size={18} className="input-icon-left" />
                    <input
                      type="url"
                      className="form-input-enhanced with-icon"
                      value={link}
                      onChange={(e) => handleSocialLinkChange(index, e.target.value)}
                      disabled={!isEditing}
                      placeholder="https://instagram.com/yourbrand"
                    />
                  </div>
                  {formData.socialLinks.length > 1 && isEditing && (
                    <button
                      className="remove-social-btn"
                      onClick={() => removeSocialLink(index)}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isEditing && (
              <button className="add-social-btn" onClick={addSocialLink}>
                <Plus size={18} />
                Add Another Platform
              </button>
            )}
          </div>
        </div>

        {/* ===== BUSINESS KEYWORDS SECTION ===== */}
        <div className="enhanced-section-card">
          <div className="section-header-row">
            <div className="section-icon-title">
              <div className="section-icon gradient-purple">
                <Tag size={22} />
              </div>
              <div>
                <h2 className="section-main-title">Business Keywords</h2>
                <p className="section-subtitle">
                  Help customers find you — add relevant keywords
                </p>
              </div>
            </div>
          </div>

          {/* Selected Keywords Tags */}
          <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "8px", minHeight: "36px" }}>
            {selectedKeywords.length === 0 && (
              <span style={{ color: "#aaa", fontSize: "14px", alignSelf: "center" }}>
                No keywords added yet
              </span>
            )}
            {selectedKeywords.map((kw) => (
              <span
                key={kw._id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#ede9fe",
                  color: "#5b4ef3",
                  border: "1px solid #c4b5fd",
                  borderRadius: "20px",
                  padding: "4px 12px",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                <Tag size={12} />
                {kw.name}
                <button
                  onClick={() => removeKeyword(kw._id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    display: "flex",
                    color: "#7c3aed",
                    marginLeft: "2px",
                  }}
                >
                  <X size={13} />
                </button>
              </span>
            ))}
          </div>

          {/* Search & Add */}
          <div style={{ position: "relative" }} ref={dropdownRef}>
            <div className="input-with-icon">
              <Search size={16} className="input-icon-left" />
              <input
                type="text"
                className="form-input-enhanced with-icon"
                placeholder="Search or type a new keyword..."
                value={keywordSearch}
                onChange={(e) => {
                  setKeywordSearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                style={{ borderRadius: "10px" }}
              />
            </div>

            {/* Dropdown */}
            {showDropdown && (keywordSearch.trim() || filteredKeywords.length > 0) && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                  zIndex: 100,
                  maxHeight: "220px",
                  overflowY: "auto",
                }}
              >
                {keywordsLoading && (
                  <div style={{ padding: "12px 16px", color: "#888", fontSize: "13px" }}>
                    Loading...
                  </div>
                )}

                {!keywordsLoading && filteredKeywords.length === 0 && !keywordSearch.trim() && (
                  <div style={{ padding: "12px 16px", color: "#888", fontSize: "13px" }}>
                    Type to search keywords
                  </div>
                )}

                {!keywordsLoading && filteredKeywords.map((kw) => (
                  <div
                    key={kw._id}
                    onClick={() => addKeyword(kw)}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f5f3ff"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                  >
                    <Tag size={13} color="#5b4ef3" />
                    {kw.name}
                  </div>
                ))}

                {/* Create new keyword option */}
                {keywordSearch.trim() && !exactMatch && (
                  <div
                    onClick={handleCreateAndAdd}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#5b4ef3",
                      fontWeight: "600",
                      borderTop: filteredKeywords.length > 0 ? "1px solid #e5e7eb" : "none",
                      background: "#f5f3ff",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#ede9fe"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#f5f3ff"}
                  >
                    <Plus size={14} />
                    Add "{keywordSearch.trim()}" as new keyword
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Save Keywords Button */}
          <div style={{ marginTop: "16px" }}>
            <button
              className="action-btn-enhanced save-btn"
              onClick={handleSaveKeywords}
              disabled={keywordSaving}
              style={{ minWidth: "160px" }}
            >
              {keywordSaving ? (
                <>
                  <span className="btn-loader"></span>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Keywords
                </>
              )}
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-row">
          <button
            className={`action-btn-enhanced ${isEditing ? "cancel-btn" : "edit-btn-enhanced"}`}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? (
              <>
                <X size={20} />
                Cancel
              </>
            ) : (
              <>
                <Edit size={20} />
                Edit Profile
              </>
            )}
          </button>

          {isEditing && (
            <button
              className="action-btn-enhanced save-btn"
              disabled={updating}
              onClick={handleSave}
            >
              {updating ? (
                <>
                  <span className="btn-loader"></span>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Save Changes
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessOverview;
